/**
 * <product-form> – with mini-checkout support
 */
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      /* ------------------------------------------------------------ */
      /*  Life-cycle                                                 */
      /* ------------------------------------------------------------ */
      constructor() {
        super();

        /* ---------- core wiring ---------- */
        this.form         = this.querySelector('form');
        this.variantInput = this.form.querySelector('[name=id]');
        this.submitButton = this.querySelector('[type="submit"]');
        this.cart         = document.querySelector('cart-notification')
                          || document.querySelector('cart-drawer');

        if (!this.form)   return;                    // Nothing to do
        this.variantInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';

        /* ---------- mini-checkout ---------- */
        /** Element generated by {% render 'mini-checkout', section: section.id %} */
        this.miniCheckEl = document.getElementById(
          `Product-Mini-Checkout-${this.dataset.section}`
        );

        if (this.miniCheckEl) {
          /** 
           * When the *top* of the <product-form> leaves the viewport
           * (rootMargin = -200 px keeps it a little later) we show the
           * floating mini-checkout.  When the form is visible again we hide
           * it.
           */
          this.miniCheckObserver = new IntersectionObserver(
            (entries) => {
              const [entry] = entries;
              if (entry.isIntersecting) {
                this.hideMiniCheck();
              } else if (entry.boundingClientRect.top < 0) {
                this.showMiniCheck();
              }
            },
            { root: null, rootMargin: '-100px 0px 0px 0px' }
          );
          
          this.miniCheckObserver.observe(this);
        
          // —— NEW: Update mini-checkout on variant change ——
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            (event) => {
              const { sectionId, variant, html } = event.data;
              // Only update if this is our mini-checkout
              if (sectionId !== this.dataset.section) return;
        
              // —————— IMAGE UPDATE ——————
              const imgEl = this.miniCheckEl.querySelector('.product-image img');
              if (imgEl && variant.featured_media?.preview_image?.src) {
                const newImgSrc = variant.featured_media.preview_image.src;
                // only swap if different
                if (imgEl.src !== newImgSrc) {
                  imgEl.src    = newImgSrc;
                  imgEl.srcset = '';
                }
              }
        
              // —————— PRICE UPDATE ——————
              const priceSource = html.querySelector(`#price-${sectionId}`);
              const priceTarget = this.miniCheckEl.querySelector('.mini-atc-price');
              
              if (priceSource && priceTarget) {
                // Grab the sale‐price span; if you want regular when no sale, you could
                // fall back to `.price-item--regular` here instead.
                const saleSpan = priceSource.querySelector('.price-item--sale');
                if (saleSpan) {
                  // 1) Get the raw text, e.g. "£19.99 GBP"
                  const rawPrice = saleSpan.textContent.trim();
              
                  // 2) Remove the trailing " GBP" (or any 3-letter code)
                  //    Method A: split/join
                  // const parts   = rawPrice.split(' ');
                  // const cleaned = parts.slice(0, -1).join(' ');
              
                  //    Method B: regex (space + last word)
                  const cleaned = rawPrice.replace(/\s+\S+$/, '');
              
                  // 3) Inject only the symbol+amount
                  priceTarget.textContent = cleaned;
                }
              }
        
              // —————— TITLE UPDATE ——————
              const titleTarget = this.miniCheckEl.querySelector('small.light');
              if (titleTarget && variant.title) {
                let titleText = variant.title.trim();
                if (titleText.length > 15) {
                  titleText = titleText.slice(0, 12) + '…';
                }
                titleTarget.textContent = titleText;
              }
            }
          );
        }
      }

      disconnectedCallback() {
        if (this.miniCheckObserver) this.miniCheckObserver.disconnect();
      }

      /* ------------------------------------------------------------ */
      /*  Submit                                                      */
      /* ------------------------------------------------------------ */
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      /* ------------------------------------------------------------ */
      /*  Helpers                                                     */
      /* ------------------------------------------------------------ */
      showMiniCheck() {
        if (this.miniCheckEl) this.miniCheckEl.classList.add('active');
      }
      hideMiniCheck() {
        if (this.miniCheckEl) this.miniCheckEl.classList.remove('active');
      }
      showSoldOutState() {
        const soldOutMsg = this.submitButton.querySelector('.sold-out-message');
        if (!soldOutMsg) return;
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.querySelector('span').classList.add('hidden');
        soldOutMsg.classList.remove('hidden');
      }

      handleErrorMessage(errorMessage = '') {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper
          || this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage
          || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}
